﻿import { ProductViewModelFactories } from './ProductViewModelFactories.js';

export const ProductMaterialsLoadMode = {
  Never: 'Never',
  Delayed: 'Delayed', // After a timeout...
  AsyncNow: 'AsyncNow', // Immediately...
};

/**
 * This is a ViewModel which contains a Product drawing. It uses composition by
 * accepting a ProductDrawing object managing that drawing, while adding additional UI.
 */
export class ProductDrawingViewModel {
  constructor(drawing, name) {
    this.name = name || 'ProductDrawingViewModel';
    this.drawing = drawing;
    if (drawing) {
      drawing.onProductDisplayed = (viewModel, product) => this.onProductDisplayed(viewModel, product);
    }
    this.apiProduct = null;
    this.isStarted = false;
    this.isStarting = false;
    this.productMaterialsLoadMode = ProductMaterialsLoadMode.Never;
    this.displayProductWhenStarting = true;
  }

  isDrawingVisible() {
    return false;
  }

  /**
   * This is a handler for this.drawing.onProductDisplayed event.
   */
  onProductDisplayed(drawing, product) {
  }

  doBeforeDisplayProduct(data) {
  }

  doAfterDisplayProduct(data) {
  }

  //logApiProductGraph(product) {
  //  const rect = product.rect;
  //  const name = product.constructor.name;
  //  const label = `${name}: l=${rect.left}, b=${rect.bottom}, w=${rect.width}, h=${rect.height}`;
  //  if (product.components.length == 0) {
  //    console.log(label);
  //  } else {
  //    console.groupCollapsed(label);
  //    product.components.forEach(child => this.logApiProductGraph(child));
  //    console.groupEnd();
  //  }
  //}

  //logComponentGraph(component) {
  //  const rect = component.getTopLeftRect();
  //  const name = component.constructor.name;
  //  const label = `${name}: l=${rect.left}, t=${rect.top}, w=${rect.width}, h=${rect.height}`;
  //  if (component.components.length == 0) {
  //    console.log(label);
  //  } else {
  //    console.groupCollapsed(label);
  //    component.components.forEach(child => this.logComponentGraph(child));
  //    console.groupEnd();
  //  }
  //}

  scheduleProductDisplay(isUserDriven, onDisplay) {
    if (!this.pendingDisplayCalls) {
      this.pendingDisplayCalls = [];
    }
    this.pendingDisplayCalls.push({ isUserDriven, onDisplay });
  }

  async displayProduct(apiProduct, data) {
    // Optimization (added 8/25/2020):
    if (!this.isStarted && !this.isStarting) {
      // displayProduct will be called from within this.Start()
      return;
    }
    // Optimization (added 8/25/2020):
    if (!this.isDrawingVisible()) {
      // Assume we don't need to display the product until the drawing is visible
      return;
    }

    if (this.isDisplayingProduct) {
      // We don't allow re-entrant calls to this method!
      throw new Error(`Re-entrant call to ${this.name}.displayProduct method.`);
    }
    this.isDisplayingProduct = true;
    try {
      this.apiProduct = apiProduct;
      if (!this.apiProduct) {
        return;
      }

      this.doBeforeDisplayProduct(data);

      const product = ProductViewModelFactories.createProduct(this.drawing.drawingService, this.apiProduct);

      // Let's get the resource loading started
      const materialIdsToLoad = product.createMaterialIdList();
      console.group(`${this.name}.displayProduct: Awaiting drawing.loadMaterials(...) for product ${product.name}...`);
      await this.drawing.loadMaterials(materialIdsToLoad, /*high res as well?*/ true);
      console.groupEnd();
      //console.log(`${this.name}.displayProduct: drawing.loadMaterials(...) completed. Next, drawing.displayProduct(...)`);
      // Display the product
      //this.logApiProductGraph(product);
      console.group(`${this.name}.displayProduct: drawing.displayProduct()...`);
      this.drawing.displayProduct(product);
      console.groupEnd();

      this.doAfterDisplayProduct(data);
      //this.logComponentGraph(product);
    }
    finally {
      this.isDisplayingProduct = false;
      if (this.pendingDisplayCalls && this.pendingDisplayCalls.length > 0) {
        const rec = this.pendingDisplayCalls.pop();
        rec.onDisplay(rec.isUserDriven);
      }
    }
  }

  async displayCurrentProduct(data) {
    await this.displayProduct(this.apiProduct, data);
  }

  doAfterHidingCurrentProduct(data) {
  }

  /**
   * Can be called by the user to indicate that the UI no longer needs to show the product. Sub
   * classes can override the doAfterHidingCurrentProduct() method to detach from events, etc.
   */
  hideCurrentProduct(data) {
    this.doAfterHidingCurrentProduct(data);
  }

  /** Asynchronously loads ALL the materials we expect will be needed in the app for the current
   * product type. */
  async loadProductMaterials() {
    const factory = ProductViewModelFactories.getFactory(this.apiProduct.type);
    const materialIds = factory.getAllKnownProductMaterialIds(this.apiProduct);
    //console.log('Awaiting additional materials to be loaded...');
    const loadingMaterials = await this.drawing.loadMaterials(materialIds, /*high res as well?*/ false);
    //console.log('Additional materials load completed!');
    return loadingMaterials;
  }

  async doStart(apiProduct, data) {
    /* This is also done inside displayProduct, but we explicitly make the assignment here because
     * the call to loadProductMaterials below is dependent on it. */
    this.apiProduct = apiProduct;
    if (this.displayProductWhenStarting) {
      console.log(`${this.name}.doStart: awaiting displayProduct()...`);
      await this.displayProduct(apiProduct, data);
      console.log(`${this.name}.doStart: displayProduct() completed!`);
    }

    switch (this.productMaterialsLoadMode) {
      case ProductMaterialsLoadMode.AsyncNow:
        // Load additional materials we expect will be needed in the app now, but do it asynchronously:
        /*await*/ this.loadProductMaterials();
        //console.log('Continuing while additional materials are being loaded...');
        break;
      case ProductMaterialsLoadMode.Delayed:
        /* We don't want to start downloading all materials yet because of the negative impact it has
         * on SEO Core Web Vitals. Instead set a long timer to start downloading after we have reached
         * TTI (Time To Interactive). 10/1/2021. */
        setTimeout(() => /*await*/ this.loadProductMaterials(), 10000);
        break;
    }
  }

  async start(apiProduct, data) {
    if (!this.isStarted) {
      this.isStarting = true;
      await this.doStart(apiProduct, data);
      //console.log(`${this.name} started!`);
      this.isStarting = false;
      this.isStarted = true;
    }
  }
}
